import { Field, Fields } from "redux-form";

import { Box, Typography } from "@braintrust/braintrust-ui-components";
import {
  JOB_PAYMENT_TYPE_LABELS,
  OFFER_FIELDS,
} from "@js/apps/jobs/apps/offers/constants";
import type { CreateOrEditOfferFormData } from "@js/apps/jobs/apps/offers/types";
import {
  getLabelForWorkingHourOption,
  getPaymentMethodContent,
} from "@js/apps/jobs/apps/offers/utils";
import { ReviewBeforeSendingSummaryItem } from "@js/apps/jobs/components";
import { DateComponent } from "@js/components/date";
import { MarkdownContent } from "@js/components/markdown-content";
import type { TypedWrappedFieldInputProps } from "@js/forms/utils";
import { formatCurrency } from "@js/utils";
import type { IsoDate } from "@js/utils/date/types";
import { DateFormats } from "@js/utils/date/types";

export const PayFieldItem = () => {
  return (
    <Fields
      names={[OFFER_FIELDS.payment_amount, OFFER_FIELDS.payment_type]}
      component={Pay}
    />
  );
};

export const DatesFieldItem = () => {
  return (
    <Fields
      names={[OFFER_FIELDS.proposed_start_date, OFFER_FIELDS.proposed_end_date]}
      component={Dates}
    />
  );
};

export const CommitmentFieldItem = () => {
  return (
    <Fields
      names={[
        OFFER_FIELDS.anticipated_weekly_hours,
        OFFER_FIELDS.hours_limit_type,
        OFFER_FIELDS.pre_approve_extra_hours,
        OFFER_FIELDS.availability_details,
      ]}
      component={Commitment}
    />
  );
};

export const EquipmentSummaryFieldItem = () => {
  return (
    <Field
      name={OFFER_FIELDS.talent_has_own_equipment}
      component={EquipmentSummary}
    />
  );
};

export const AdditionalPaperworkFieldItem = () => {
  return (
    <Field
      name={OFFER_FIELDS.require_paperwork}
      component={AdditionalPaperwork}
    />
  );
};

export const PaymentMethodFieldItem = () => {
  return (
    <Field
      name={OFFER_FIELDS.deposit_payment_method}
      component={PaymentMethod}
    />
  );
};

type FieldProps<K extends keyof CreateOrEditOfferFormData> = {
  input: TypedWrappedFieldInputProps<CreateOrEditOfferFormData[K]>;
};

const EquipmentSummary = ({
  input,
}: FieldProps<"talent_has_own_equipment">) => {
  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Equipment summary"
      sectionContent={
        input.value
          ? "Talent will use their own equipment"
          : "Some equipment will be provided"
      }
    />
  );
};

const AdditionalPaperwork = ({ input }: FieldProps<"require_paperwork">) => {
  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Additional Paperwork"
      sectionContent={input.value ? "Yes" : "No"}
    />
  );
};

type PayProps = {
  payment_amount: FieldProps<"payment_amount">;
  payment_type: FieldProps<"payment_type">;
};

const Pay = ({ payment_amount, payment_type }: PayProps) => {
  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Pay"
      sectionContent={`${formatCurrency(payment_amount.input.value ?? 0)} ${
        JOB_PAYMENT_TYPE_LABELS[payment_type.input.value]
      }`}
    />
  );
};

type DatesProps = {
  proposed_start_date: FieldProps<"proposed_start_date">;
  proposed_end_date: FieldProps<"proposed_end_date">;
};

const Dates = ({ proposed_start_date, proposed_end_date }: DatesProps) => {
  const startDate = proposed_start_date.input.value as IsoDate;

  const endDate = proposed_end_date.input.value as IsoDate;

  const content = (
    <>
      {startDate && (
        <>
          Starts{" "}
          <DateComponent date={startDate} format={DateFormats["Jan 1, 1970"]} />
          ,{" "}
        </>
      )}
      {endDate && (
        <>
          Ends{" "}
          <DateComponent date={endDate} format={DateFormats["Jan 1, 1970"]} />
        </>
      )}
    </>
  );

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Dates"
      sectionContent={content}
    />
  );
};

type CommitmentProps = {
  anticipated_weekly_hours: FieldProps<"anticipated_weekly_hours">;
  hours_limit_type: FieldProps<"hours_limit_type">;
  pre_approve_extra_hours: FieldProps<"pre_approve_extra_hours">;
  availability_details: FieldProps<"availability_details">;
};

const Commitment = ({
  anticipated_weekly_hours,
  hours_limit_type,
  pre_approve_extra_hours,
  availability_details,
}: CommitmentProps) => {
  const getSectionContent = () => {
    let content = getLabelForWorkingHourOption({
      option: hours_limit_type.input.value,
      anticipatedHours: String(anticipated_weekly_hours.input.value),
      withHours: true,
    });

    if (pre_approve_extra_hours.input.value) {
      content += "\nAdditional hours must be approved";
    }
    if (availability_details.input.value) {
      content += `\n${availability_details.input.value}`;
    }

    return content;
  };

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Commitment"
      sectionContent={
        <Typography
          component="span"
          variant="paragraph"
          size="small"
          sx={{
            whiteSpace: "pre-wrap",
            display: "inline-block",
            ml: 1,
            overflowWrap: "anywhere",
          }}
        >
          {getSectionContent()}
        </Typography>
      }
      ellipsis={false}
    />
  );
};

export const AdditionalTermsFieldItem = () => {
  return (
    <Field
      name={OFFER_FIELDS.additional_contract_terms}
      component={AdditionalTerms}
    />
  );
};

const AdditionalTerms = ({
  input,
}: FieldProps<"additional_contract_terms">) => {
  const value = input.value;
  if (!value) {
    return <></>;
  }

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="Additional terms"
      sectionContent={
        <Box
          sx={{
            ml: 1,
            overflowWrap: "anywhere",
            overflow: "hidden",
            "*": { fontSize: "0.8125rem", lineHeight: "1.8461538462" },
          }}
        >
          <MarkdownContent markdown={value} />
        </Box>
      }
      ellipsis={false}
    />
  );
};

const PaymentMethod = ({ input }: FieldProps<"deposit_payment_method">) => {
  if (!input.value) return null;

  const content = getPaymentMethodContent(input.value);

  return (
    <ReviewBeforeSendingSummaryItem
      sectionName="$500 Deposit details"
      sectionContent={content}
    />
  );
};
